import { Table } from "react-bootstrap";
import styled from "styled-components";
import { palette } from "../../styles/theme/colors";
import classNames from "classnames";

type StyledTableProps = {
  hasMorePaddingOnFirstElement?: boolean;
  isOverviewPage?: boolean;
  hasMarginBottom?: boolean;
  hover?: boolean;
  hoverColor?: string;
  borderBottom?: string;
  hasStickyFirstColumn?: boolean;
  hasColumnTitlesClickable?: boolean;
};

/*
 * Generally we'll want to keep td at font-size 12px and line-height 22px, but this is table specific. There are certain rows
 * that should be different, and if so, simply override this.
 *
 * For our Issues and Logs table, we'll want 24px of margin on the first element on the left-hand side
 * For our Data Sync table, we'll want to keep 16px of margin on the left-hand side
 */

const StyledTable = styled.div<StyledTableProps>`
  box-shadow:${(props) => (props.isOverviewPage ? "" : "0px 4px 20px -4px rgba(0, 0, 0, 0.08);")} 
  background: ${palette.white};
  max-width: 100%;
  overflow-x: auto;
  && th:first-child,
  && td:first-child {
    padding-left: ${({ hasMorePaddingOnFirstElement }) =>
      hasMorePaddingOnFirstElement ? "24px" : "16px"};
  }

  && th:last-child {
    margin: -2px;
  }
  && td:last-child {
    margin: -0.5px;
  }

  && th {
    background: ${palette.white};
    color: ${palette.black};
    font-size: 14px;
    line-height: 24px;
    padding: 16px 24px;
    ${(props) =>
      props.hasColumnTitlesClickable &&
      `
        cursor: pointer;
        user-select: none;
        transition: background 0.2s;
      `}
  }
  ${(props) =>
    props.hasColumnTitlesClickable &&
    `
      && th:hover,
      && th:focus-within {
        background: #f3f4f6; /* Tailwind gray-100 */
      }
    `}
  ${(props) =>
    props.hasStickyFirstColumn &&
    props.hasColumnTitlesClickable &&
    `
      && th:first-child:hover,
      && th:first-child:focus-within {
        background: #f3f4f6; /* Tailwind gray-100 */
      }
    `}
  && td {
    padding: 20px 24px;
    font-size: 12px;
    line-height: 22px;
  }

  // Specifying multi-line-text as a class on a td will allow the text to wrap
  && th, && td.multi-line-text {
    max-width: 200px;
    white-space: normal;
    word-wrap: break-word;
  }

  
  // Add hover effect color
  ${(props) =>
    props.hover && props.hoverColor && `&& tbody tr:hover { background: ${props.hoverColor}; }`}

  // Optionally add border-bottom to the elements
  ${(props) =>
    props.borderBottom && `&& th, && tr, && td { border-bottom: ${props.borderBottom}; }`}

  // Optionally make the first column sticky and add a divider line
  ${(props) =>
    props.hasStickyFirstColumn &&
    `
      && th:first-child,
      && td:first-child {
        left: 0; /* Ensures it remains sticky */
        z-index: 10;
        background: white; /* Default background */
        position: sticky;
      }
  
      && th:first-child::after,
      && td:first-child::after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: .5px; /* Width of the border */
        background-color: #f3f4f6; /* Equivalent to bg-gray-200 */
      }
  
      && tbody tr:hover th:first-child,
      && tbody tr:hover td:first-child {
        background: ${props.hoverColor}
      }
    `}
  

/* Additional styles for hover can be applied above if needed */

`;

type Props = {
  className?: string;
  header?: JSX.Element;
  content: JSX.Element | JSX.Element[];
  hasMorePaddingOnFirstElement?: boolean;
  isOverviewPage?: boolean;
  hasMarginBottom?: boolean;
  hover?: boolean;
  hoverColor?: string;
  borderBottom?: string;
  hasStickyFirstColumn?: boolean;
  hasColumnTitlesClickable?: boolean;
};

const MergeTable = ({
  className,
  header,
  content,
  hasMorePaddingOnFirstElement = false,
  isOverviewPage = false,
  hasMarginBottom = true,
  hover = true,
  hoverColor,
  borderBottom,
  hasStickyFirstColumn,
  hasColumnTitlesClickable,
}: Props) => {
  return (
    <StyledTable
      className={classNames(
        "table-responsive rounded-table-corner rounded-lg fs-mask",
        hasMarginBottom && "mb-5",
        hover && "[&>tr]:cursor-pointer",
        className
      )}
      hasMorePaddingOnFirstElement={hasMorePaddingOnFirstElement}
      isOverviewPage={isOverviewPage}
      hoverColor={hoverColor}
      hover={hover}
      borderBottom={borderBottom}
      hasStickyFirstColumn={hasStickyFirstColumn}
      hasColumnTitlesClickable={hasColumnTitlesClickable}
    >
      <Table size="sm" hover={hover} className="table-nowrap mb-0">
        {header && (
          <thead className="table-borderless">
            <tr>{header}</tr>
          </thead>
        )}
        <tbody>{content}</tbody>
      </Table>
    </StyledTable>
  );
};

export default MergeTable;
